<template>
    <header v-if="showNewVersion" class="overview-header" data-bot="overview__header">
        <div class="overview-header__backlink">
            <CompassBackForwardButton
                :label="$t.__('common.actions.back')"
                :link="'javascript:history.back();'"
                type="secondary"
                size="small"
                back
            />
            <OverviewNav
                :overviewhash="overviewhash"
                :identity="identity"
                :splitter="splitter"
                :useSPARouting="true"
                class="flex-right"
            />
        </div>
        <div class="overview-header__wrapper">
            <div class="overview-header__title-and-tags">
                <EditText
                    class="overview-header__title"
                    :text="name"
                    :canEdit="canEditName"
                    :loading="isLoadingTitle"
                    contentTypographyClass="typographies-heading-h1"
                    @update="handleTitleUpdate"
                >
                    <h2 class="overview-header__title-content typographies-heading-h1">
                        <span data-bot="overview__header--name">
                            {{ name }}
                        </span>
                    </h2>
                </EditText>
                <div v-if="newTagsThird.length > 0" class="overview-header__third-tags">
                    <TagThird
                        v-for="(tag, index) in newTagsThird"
                        :key="index"
                        :type="tag.type"
                        size="extra-small"
                        :data-bot="`overview__header--label-${index + 1}`"
                    >
                        {{ tag.label }}
                    </TagThird>
                </div>
                <tags
                    :linkedtype="linkedtype"
                    :linkedid="linkedid"
                    :permissions="permissionsTag"
                    :options="tags"
                    display="inline"
                >
                    <template #title><span></span></template>
                </tags>
            </div>
            <div class="overview-header__stats-and-actions">
                <div class="overview-header__stats-and-avatar">
                    <div
                        v-if="isClient"
                        class="overview-header__stats"
                        data-bot="overview__header--total-turnover"
                    >
                        <router-link :to="{ name: 'third.overview.statistics' }">
                            <fiscal-statistics-header
                                :linkedtype="linkedtype"
                                :linkedid="linkedid"
                            />
                        </router-link>
                    </div>
                    <CompassFormItem size="small" :label="$t.__('common.labels.owner')">
                        <EditOwner
                            :data="[owner]"
                            :canEdit="hasAccess('can_edit', permissions)"
                            :multiple="false"
                            :loading="isLoadingOwner"
                            @update="handleOwnerUpdate"
                        />
                    </CompassFormItem>
                </div>
                <div class="overview-header__actions">
                    <CompassMenu placement="bottom-end" customClass="overview-header__actions-menu">
                        <CompassIconButton
                            icon="action"
                            data-bot="overview-header__actions-button"
                        />
                        <template #dropdown>
                            <CompassDropdownOption
                                v-for="(action, index) in mapActions"
                                :key="index"
                                :disabled="!hasAccess(action.permission, permissions)"
                                :label="action.label"
                                @itemSelected="handleActionItemClick(action)"
                            />
                        </template>
                    </CompassMenu>
                    <CompassMenu
                        v-if="showCreateMenu"
                        placement="bottom-end"
                        customClass="overview-header__create-menu"
                    >
                        <CompassButton
                            :label="$t.__('common.actions.create')"
                            type="secondary"
                            size="small"
                            dropdown
                        />
                        <template #dropdown>
                            <CompassDropdownOption
                                v-for="(action, index) in allowedCreateActions"
                                :key="index"
                                :label="action.label"
                                @itemSelected="handleCreate(action.type)"
                            />
                        </template>
                    </CompassMenu>
                    <CompassMenu
                        placement="bottom-end"
                        customClass="overview-header__schedule-menu"
                    >
                        <CompassButton
                            :label="$t.__('common.actions.schedule')"
                            size="small"
                            type="secondary"
                            dropdown
                        />
                        <template #dropdown>
                            <CompassDropdownOption
                                v-for="(action, index) in allowedScheduledActions"
                                :key="index"
                                @itemSelected="handleClick(action.type)"
                            >
                                <CompassObject :object="action.icon" size="extra-small" />
                                <span>{{ action.label }}</span>
                            </CompassDropdownOption>
                        </template>
                    </CompassMenu>
                </div>
            </div>
        </div>
    </header>
    <header v-else class="white-bkg bb-1 px-5 py-3 mt--5 ml--5" data-bot="overview__header">
        <div class="flex-level m-auto pb-3">
            <!-- IMG AND NAME -->
            <div class="identity flex-level no-shrink flex-top wp-10">
                <!-- Avatar -->
                <avatar
                    v-show="avatar.type == 'picture'"
                    :avatar="avatar"
                    size="xxbig"
                    :editable="hasAccess('can_edit', permissions)"
                    :route="avatarRoute"
                />

                <!-- Name & Type -->
                <div class="wp-10" :class="{ 'ml-3': avatar.type == 'picture' }">
                    <h2 class="mt-0 mb-1 ellipsis">
                        <span class="flex-level">
                            <el-tooltip effect="dark" :content="name" placement="top">
                                <span class="mr-2" data-bot="overview__header--name">
                                    {{ name }}
                                </span>
                            </el-tooltip>
                            <TagThird
                                v-for="(tagThird, index) in tagsThirdtype"
                                :key="index"
                                class="tag-third"
                                :type="tagThird.type"
                                size="extra-small"
                                :data-bot="`overview__header--label-${index + 1}`"
                            >
                                {{ tagThird.label }}
                            </TagThird>
                        </span>
                    </h2>
                    <tags
                        :linkedtype="linkedtype"
                        :linkedid="linkedid"
                        :permissions="permissionsTag"
                        :options="tags"
                        display="inline"
                        class="flex-row flex-right m-0 flex-row-reverse ai-center tags-350"
                    >
                        <template #title><span></span></template>
                    </tags>
                </div>

                <div
                    v-if="isClient"
                    class="flex-item-right aright header-statistics"
                    data-bot="overview__header--total-turnover"
                >
                    <router-link :to="{ name: 'third.overview.statistics' }" class="gray-100">
                        <fiscal-statistics-header :linkedtype="linkedtype" :linkedid="linkedid" />
                    </router-link>
                </div>
            </div>
        </div>
        <div class="flex-level m-auto pt-2 bt-1">
            <div class="main-infos flex-row flex fw-wrap">
                <div v-for="(item, index) in favorites" :key="index" class="data-block pl-0">
                    <component
                        :is="item.format + '-data'"
                        v-bind="item"
                        :canRemoveFavorite="true"
                        @toggleFavorite="updateFavorites(item)"
                    >
                    </component>
                </div>
            </div>

            <!-- CTA -->
            <div class="flex-right">
                <el-dropdown>
                    <CompassButton
                        :label="$t.__('common.labels.actions')"
                        type="secondary"
                        dropdown
                    />
                    <template #dropdown>
                        <el-dropdown-menu>
                            <el-dropdown-item
                                v-for="(action, index) in mapActions"
                                :key="index"
                                :disabled="!hasAccess(action.permission, permissions)"
                            >
                                <a
                                    v-if="action.type == 'function'"
                                    :class="
                                        !hasAccess(action.permission, permissions)
                                            ? 'link-disabled'
                                            : ''
                                    "
                                    @click="handleFunctionCall(action.value)"
                                >
                                    {{ action.label }}
                                </a>
                                <a
                                    v-else-if="action.type == 'vuejs'"
                                    :onclick="buildUrl(action.value, parameters)"
                                    :class="
                                        !hasAccess(action.permission, permissions)
                                            ? 'link-disabled'
                                            : ''
                                    "
                                >
                                    {{ action.label }}
                                </a>
                                <a
                                    v-else
                                    :href="buildUrl(action.value, parameters)"
                                    :class="
                                        !hasAccess(action.permission, permissions)
                                            ? 'link-disabled'
                                            : ''
                                    "
                                >
                                    {{ action.label }}
                                </a>
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </template>
                </el-dropdown>
            </div>
        </div>
    </header>
</template>

<script>
import { formatUrl, buildUrl, hasAccess } from '../../helpers';
import Text from './tabs/data/Text.vue';
import Textarea from './tabs/data/Textarea.vue';
import Link from './tabs/data/Link.vue';
import ExternalLink from './tabs/data/ExternalLink.vue';
import Phone from './tabs/data/Phone.vue';
import Email from './tabs/data/Email.vue';
import HumanNameTypeMixin from '../../mixins/HumanNameTypeMixins';
import Avatar from 'vcomponents/utilities/Avatar.vue';
import Tags from 'vcomponents/utilities/Tags.vue';
import TagRounded from 'vcomponents/utilities/TagRounded.vue';
import FiscalStatisticsHeader from './FiscalStatisticsHeader.vue';
import TagHumansConfig from 'Config/authenticated/TagHumans';
import { mapState } from 'vuex';
import { createNamespacedHelpers } from 'vuex';
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
import Feature from 'Services/Feature';
import ThirdTypeTags from 'Dictionaries/thirdTypeTags';
import { randAvatarColorFullname } from 'utils/avatar';
import EmailMixins from 'mixins/EmailMixins';
import EditText from 'vcomponents/common/editInPlace/Text/Text.vue';
import CompanyApi from 'api/companies';
import ContactApi from 'api/contacts';
import { updateIndividual } from 'api/individuals';
import EditOwner from 'vcomponents/common/editInPlace/Avatar/Avatar.vue';
import OverviewNav from 'vcomponents/overview/OverviewNav.vue';
import TagThird from 'vcomponents/common/TagThird/TagThird.vue';

export default {
    components: {
        'text-data': Text,
        'textarea-data': Textarea,
        'link-data': Link,
        'external_url-data': ExternalLink,
        'phone-data': Phone,
        'email-data': Email,
        Avatar,
        Tags,
        FiscalStatisticsHeader,
        TagRounded,
        EditText,
        EditOwner,
        OverviewNav,
        TagThird,
    },
    mixins: [HumanNameTypeMixin, EmailMixins],
    props: {
        overviewhash: {
            type: String,
            required: true,
            default: '',
        },
        actions: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            scheduledActions: [
                {
                    icon: 'sent-email',
                    type: 'email',
                    label: this.$t.__('overview.header.scheduleActions.email'),
                    permission: true,
                },
                {
                    icon: 'task',
                    type: 'task',
                    label: this.$t.__('overview.header.scheduleActions.task'),
                    permission: true,
                },
                {
                    icon: 'calendar',
                    type: 'event',
                    label: this.$t.__('overview.header.scheduleActions.event'),
                    permission: true,
                },
                {
                    icon: 'phone',
                    type: 'phonecallScheduled',
                    label: this.$t.__('overview.header.scheduleActions.call'),
                    permission: this.$store.state.corp.plugins.includes('phonecall'),
                },
            ],
            createActions: [],
            isLoadingTitle: false,
            isLoadingOwner: false,
            splitter: /\/(\d+)/,
        };
    },
    computed: {
        ...mapState('corp', {
            corpid: 'id',
        }),
        canEditName() {
            return this.permissions.can_edit && !this.isIndividual && !this.isContact;
        },
        mapActions() {
            const actionsList = [];
            this.actions.forEach((action) => {
                if (this.hasAccess(action.display, this.permissions)) {
                    actionsList.push(action);
                }
            });
            return actionsList;
        },
        showNewVersion() {
            return Feature.isEnabled('overview-info-sidebar');
        },
        owner() {
            return this.$store.getters.owner;
        },
        ownerInitials() {
            return `${this.owner.firstname.charAt(0)}${this.owner.lastname.charAt(0)}`;
        },
        avatar() {
            return this.$store.getters.avatar;
        },
        identity() {
            return this.$store.getters.identity;
        },
        informations() {
            return this.$store.getters.dataFetch;
        },
        favorites() {
            return this.$store.getters.favorites;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        linkedid() {
            return this.$store.getters.linkedid;
        },
        linkedtype() {
            return this.$store.getters.linkedtype;
        },
        permissionsTag() {
            return {
                can_edit: this.permissions['can_edit'] ? this.permissions['can_edit'] : false,
                can_create: this.permissions['can_create_new_tag_value']
                    ? this.permissions['can_create_new_tag_value']
                    : false,
            };
        },
        tags() {
            return this.$store.getters.tags;
        },
        parameters() {
            return [
                {
                    key: 'id',
                    value: this.$store.getters.linkedid,
                },
                {
                    key: 'csrf_token',
                    value: this.$store.getters.csrfToken,
                },
                {
                    key: 'type',
                    value: this.$store.getters.linkedtype,
                },
                {
                    key: 'can_delete',
                    value: this.$store.getters.permissions['can_delete'],
                },
                {
                    key: 'can_delete_people',
                    value: this.$store.getters.permissions['can_delete_people'] ? 'Y' : 'N',
                },
                {
                    key: 'corpid',
                    value: this.corpid,
                },
                {
                    key: 'main_tpcid',
                    value:
                        this.$store.getters.mainContact !== undefined
                            ? this.$store.getters.mainContact.id
                            : null,
                },
                {
                    key: 'typeid',
                    value: this.identity.type,
                },
            ];
        },
        name() {
            return this.identity.civility
                ? this.identity.civility + ' ' + this.identity.name
                : this.identity.name;
        },
        avatarRoute() {
            return `/rest/${this.$store.getters.linkedtype}s/${this.$store.getters.linkedid}/logo`;
        },
        routes() {
            return this.$store.getters.routes;
        },
        isThird() {
            if (['client', 'prospect', 'supplier'].includes(this.identity.type)) {
                return true;
            }
            return false;
        },
        isSupplier() {
            return (
                this.linkedtype === 'third' && this.$store.state.third.identity.type === 'supplier'
            );
        },
        isContact() {
            return (
                !this.identity.hasOwnProperty('isCorporation') && this.identity.type === 'contact'
            );
        },
        isIndividual() {
            if (
                ['client', 'prospect', 'supplier'].includes(this.identity.type) &&
                !this.$store.getters.identity.isCorporation
            ) {
                return true;
            }
            return false;
        },
        hasPersonTag() {
            if (this.isThird && !this.identity.isCorporation) {
                return true;
            }
            return false;
        },
        isCompany() {
            return this.isThird && !this.hasPersonTag;
        },
        isParticular() {
            return this.isThird && this.hasPersonTag;
        },
        isArchived() {
            return this.identity.isArchived;
        },
        isClient() {
            return ['client'].includes(this.identity.type);
        },
        tagsThirdtype() {
            const tagsThirdtype = [];

            if (this.isCompany) {
                tagsThirdtype.push({ ...TagHumansConfig['company'], type: 'company' });
            }
            if (this.isParticular) {
                tagsThirdtype.push({ ...TagHumansConfig['person'], type: 'individual' });
            }
            tagsThirdtype.push({
                ...TagHumansConfig[this.identity.type],
                type: this.identity.type,
            });

            if (this.isArchived) {
                tagsThirdtype.push({ ...TagHumansConfig['archived'], type: 'archived' });
            }

            return tagsThirdtype;
        },
        newTagsThird() {
            const tagMap = {
                isCompany: 'company',
                isParticular: 'person',
                isArchived: 'archived',
            };

            const tags = Object.entries(tagMap)
                .filter(([key]) => this[key])
                .map(([, value]) => ThirdTypeTags[value]);

            tags.push(ThirdTypeTags[this.identity.type]);

            return tags;
        },
        allowedScheduledActions() {
            return this.scheduledActions.filter((item) => item.permission);
        },
        allowedCreateActions() {
            return this.createActions.filter((item) => item.permission);
        },
        userPermissions() {
            return this.$store.state.user.permissions.acl;
        },
        canCreateOpportunity() {
            return this.userPermissions.create_opportunities;
        },
        canCreateEstimate() {
            return this.userPermissions.create_estimates;
        },
        canCreateInvoice() {
            if (this.isSupplier) {
                return this.hasPurchasingModule && this.userPermissions.create_purchase_invoices;
            }
            return this.userPermissions.create_invoices;
        },
        canCreatePurchaseOrder() {
            if (this.isSupplier) {
                return this.hasPurchasingModule && this.userPermissions.create_purchase_orders;
            }
            return this.userPermissions.create_orders;
        },
        canCreateDeliveryOrder() {
            if (this.isSupplier) {
                return this.hasPurchasingModule && this.userPermissions.create_purchase_deliveries;
            }
            return this.userPermissions.create_deliveries;
        },
        hasPurchasingModule() {
            return this.$store.state.corp.plugins.includes('purchases');
        },
        hasInvoicingModule() {
            return this.$store.state.corp.plugins.includes('invoicing');
        },
        showCreateMenu() {
            return !this.isContact && this.allowedCreateActions.length > 0;
        },
    },
    created() {
        this.initCreateActions();
    },
    methods: {
        ...popupPanelMapActions(['open']),
        formatUrl,
        buildUrl,
        hasAccess,
        randAvatarColorFullname,
        updateFavorites(item) {
            const route = this.routes.find((route) => route.id == 'favorite');
            if (route) {
                item.favoriteName = this.buildUrl(item.favoriteName, [
                    { key: 'position', value: 0 },
                ]);
                this.$store.dispatch('updateFavorites', {
                    item: item,
                    route: this.buildUrl(route.value, [
                        { key: 'type', value: this.$store.getters.identity.type },
                    ]),
                });
            }
        },
        handleFunctionCall(function_name) {
            this[function_name]();
        },
        uploadAvatar() {
            this.open({
                key: 'avatarUploader',
                props: {
                    avatar: JSON.stringify(this.avatar),
                    route: this.avatarRoute,
                },
            });
        },
        handleActionItemClick(action) {
            switch (action.type) {
                case 'function':
                    this.handleFunctionCall(action.value);
                    break;
                case 'vuejs':
                    this.$router.push(this.buildUrl(action.value, this.parameters));
                    break;
                default:
                    window.location.href = this.buildUrl(action.value, this.parameters);
                    break;
            }
        },
        checkIsEmailSetUp() {
            this.checkEmailProvider({}, { checkPrefill: true });
        },
        handleClick(value) {
            let linkedType = this.linkedtype;

            if (
                (['task', 'phonecallScheduled'].includes(value) ||
                    (value === 'event' &&
                        Feature.isEnabled(
                            'activity-listing-crm-follow-up-deal-priotization-agenda-events',
                        ))) &&
                Feature.isEnabled('activity-new-creation-panel')
            ) {
                //We have to check if the feature is enabled because the linkedtype is not the same for apiv2
                if (this.linkedtype === 'third') {
                    linkedType = this.$store.getters.identity.isCorporation
                        ? 'company'
                        : 'individual';
                } else if (this.linkedtype === 'people') {
                    linkedType = 'contact';
                }
            }
            switch (value) {
                case 'task':
                    Activities.openActivityForm(
                        null,
                        this.linkedid,
                        linkedType,
                        function (data) {
                            window.EventBus.emit('timeline:processAndAdd', {
                                entity: 'task',
                                event: 'task.created',
                                objectid: data.id,
                            });
                        },
                        'task',
                    );
                    break;
                case 'event':
                    Activities.openActivityForm(null, this.linkedid, linkedType, null, 'event');
                    break;
                case 'email':
                    this.checkIsEmailSetUp();
                    break;
                case 'phonecallScheduled':
                    Activities.openPhoneCallForm(
                        null,
                        this.linkedid,
                        linkedType,
                        function (data) {
                            window.EventBus.emit('timeline:processAndAdd', {
                                entity: 'phonecall',
                                event: 'phonecall.created',
                                objectid: data.id,
                            });
                        },
                        true,
                    );
                    break;
            }
        },
        initCreateActions() {
            const actions = {
                opportunity: {
                    type: 'opportunity',
                    label: this.$t.__('common.labels.anOpportunity'),
                    permission: this.canCreateOpportunity,
                },
                estimate: {
                    type: 'estimate',
                    label: this.$t.__('common.labels.anEstimate'),
                    permission: this.canCreateEstimate,
                },
                invoice: {
                    type: 'invoice',
                    label: this.isSupplier
                        ? this.$t.__('common.labels.aSupplierInvoice')
                        : this.$t.__('common.labels.anInvoice'),
                    permission: this.canCreateInvoice,
                },
                order: {
                    type: 'order',
                    label: this.isSupplier
                        ? this.$t.__('common.labels.aSupplierPurchaseOrder')
                        : this.$t.__('common.labels.aPurchaseOrder'),
                    permission: this.canCreatePurchaseOrder,
                },
                deliveryOrder: {
                    type: 'deliveryOrder',
                    label: this.isSupplier
                        ? this.$t.__('common.labels.aSupplierDeliveryOrder')
                        : this.$t.__('common.labels.aDeliveryOrder'),
                    permission: this.canCreateDeliveryOrder,
                },
            };

            if (this.isThird && this.identity.type === 'prospect') {
                this.createActions.push(actions.opportunity, actions.estimate);
            } else if (this.isSupplier) {
                this.createActions.push(actions.order, actions.deliveryOrder, actions.invoice);
            } else {
                this.createActions.push(
                    actions.estimate,
                    actions.invoice,
                    actions.order,
                    actions.deliveryOrder,
                );
                if (!this.isContact) {
                    this.createActions.unshift(actions.opportunity);
                }
            }
        },
        createDocument(type, title) {
            const docType = this.isSupplier
                ? `pur${type.charAt(0).toUpperCase() + type.slice(1)}`
                : type;
            DocTools.createForThird(docType, this.$t.__(title), true, null, this.linkedid);
        },
        handleCreate(type) {
            switch (type) {
                case 'opportunity':
                    this.open({
                        key: 'opportunityForm',
                        props: {
                            oppId: false,
                            mode: 'edit',
                            linkedid: this.linkedid,
                            linkedtype: this.linkedtype,
                        },
                    });
                    break;
                case 'estimate':
                    this.createDocument('estimate', 'common.actions.create.estimate');
                    break;
                case 'invoice':
                    this.createDocument('invoice', 'common.actions.create.invoice');
                    break;
                case 'order':
                    this.createDocument('order', 'common.actions.create.order');
                    break;
                case 'deliveryOrder':
                    this.createDocument('delivery', 'common.actions.create.deliveryOrder');
                    break;
                default:
                    break;
            }
        },
        getApiMethod() {
            const apiMap = {
                individual: updateIndividual,
                client: CompanyApi.updateCompany,
                prospect: CompanyApi.updateCompany,
                supplier: CompanyApi.updateCompany,
                contact: ContactApi.updateContact,
            };

            return this.isIndividual ? apiMap.individual : apiMap[this.identity.type];
        },
        handleTitleUpdate(value) {
            this.isLoadingTitle = true;

            const apiMethod = this.getApiMethod();

            if (apiMethod) {
                this.updateTitleWithApi(apiMethod, this.linkedid, {}, { name: value });
            }
        },
        handleTitleUpdateError() {
            this.$pushToast({
                type: 'error',
                close: true,
                text: this.$t.__('overview.third.update.title.error'),
            });
        },
        async updateTitleWithApi(apiMethod, linkedid, embed, name) {
            try {
                await apiMethod(linkedid, embed, name);
                this.$store.dispatch('updateName', name.name);
                this.$pushToast({
                    type: 'success',
                    close: true,
                    text: this.$t.__('overview.third.update.title.success'),
                });
            } catch (error) {
                const errorList = Object.entries(error.response?.data?.error?.details).map(
                    ([key, value]) => `${key}: ${value.toLowerCase()}`,
                );

                if (errorList.length > 0) {
                    this.$pushToast({
                        type: 'error',
                        errorList,
                        close: true,
                        text: this.$t.__('overview.share.updateValidationError'),
                    });
                } else {
                    this.handleTitleUpdateError();
                }
            } finally {
                this.isLoadingTitle = false;
            }
        },
        handleOwnerUpdate(value) {
            this.isLoadingOwner = true;

            const apiMethod = this.getApiMethod();

            if (apiMethod) {
                apiMethod(this.linkedid, { 'embed[]': 'owner' }, { owner_id: value[0] })
                    .then((response) => {
                        const newOwner = response.data._embed.owner;
                        this.updateOwner(newOwner);
                        this.$pushToast({
                            type: 'success',
                            close: true,
                            text: this.$t.__('overview.share.update.success'),
                        });
                    })
                    .catch((error) => {
                        const errorList = [];
                        if (error.response?.data?.error?.details) {
                            errorList.push(
                                ...Object.entries(error.response?.data?.error?.details).map(
                                    ([key, value]) => `${key}: ${value.toLowerCase()}`,
                                ),
                            );
                        }

                        if (errorList.length > 0) {
                            this.$pushToast({
                                type: 'error',
                                errorList,
                                close: true,
                                text: this.$t.__('overview.share.updateValidationError'),
                            });
                        } else {
                            this.$pushToast({
                                type: 'error',
                                close: true,
                                text: this.$t.__('overview.share.updateError'),
                            });
                        }
                    })
                    .finally(() => {
                        this.isLoadingOwner = false;
                    });
            }
        },
        adaptCivility(civility) {
            return civility.toLowerCase() === 'mr'
                ? this.$t.__('common.labels.civility.mr')
                : this.$t.__('common.labels.civility.mrs');
        },
        updateOwner(newOwner) {
            this.$store.dispatch('updateOwner', {
                avatar: {
                    type: newOwner.avatar ? 'picture' : 'initials',
                    value: newOwner.avatar ?? `${newOwner.firstname[0]}${newOwner.lastname[0]}`,
                },
                value: `${newOwner.firstname[0]}${newOwner.lastname[0]}`,
                civility: newOwner.civility ? this.adaptCivility(newOwner.civility) : 'Mr',
                email: newOwner.email,
                firstname: newOwner.firstname,
                id: newOwner.id,
                lastname: newOwner.lastname,
                link: `/staff/${newOwner.id}`,
                mobile: newOwner.mobile_number ?? '',
                name: `${newOwner.firstname} ${newOwner.lastname}`,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
header {
    width: calc(100% + 56px);
}
.data-block {
    width: 25%;
}
.header-statistics {
    min-width: 300px;
}

.tag-third {
    margin-right: var(--spacing-0);
}

.overview-header {
    background: var(--colors-common-background-default);
    border-bottom: 1px solid var(--colors-common-border-lighter);
    padding: var(--spacing-3) var(--spacing-4);
    transform: translate(-24px, -32px);
    width: calc(100% + 48px);

    &__wrapper {
        display: flex;
        gap: var(--spacing-4);
    }

    &__backlink {
        display: flex;
        gap: var(--spacing-4);
        justify-content: space-between;
        margin: 0 0 var(--spacing-0);

        &::v-deep {
            .compass-link__label {
                font-size: var(--font-size-12);
                line-height: var(--line-heights-16);
            }
        }
    }

    &__title-and-tags {
        flex: 1;
        min-width: 0;

        ::v-deep {
            .label-tag {
                & > div {
                    min-width: 0;
                }
            }
            .compass-tooltip,
            .compass-tooltip__element,
            .compass-tooltip .label {
                max-width: 100%;
            }
        }
    }

    &__title {
        margin: 0 0 var(--spacing-0);

        &-content {
            margin: 0;
        }
    }

    &__third-tags {
        display: flex;
        gap: var(--spacing-0);
        margin: 0 0 var(--spacing-1);
    }

    &__stats-and-actions {
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        gap: var(--spacing-3);
        justify-content: space-between;
    }

    &__stats-and-avatar {
        display: flex;
        gap: var(--spacing-3);
        justify-content: flex-end;

        &::v-deep {
            .compass-avatar {
                flex-shrink: 0;
            }
        }
    }

    &__actions {
        align-items: center;
        display: flex;
        flex-direction: row-reverse;
        gap: var(--spacing-1);
    }
}
</style>
<style>
.overview-header__actions-menu {
    .compass-menu__dropdown__content {
        max-height: inherit;
    }
}
#compass-menu-container {
    .overview-header__create-menu {
        z-index: 10;
    }
}
</style>
